import { t } from "@lingui/macro";
import {
  ArrowCounterClockwise,
  CheckCircle,
  Fire,
  LightbulbFilament,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { cn } from "@stylingcv/utils";
import { useState } from "react";

import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";
import { toast } from "@/client/hooks/use-toast";
import { agentsService } from "@/client/services/agents.service";
import { useResumeStore } from "@/client/stores/resume";
import type { StylionRoastResult } from "@/client/pages/stylion/types";

const getRoastMessages = () => [
  t`Judging your life choices...`,
  t`Counting missing metrics...`,
  t`Consulting the employment oracle...`,
  t`Preparing royal criticism...`,
  t`Reading between the bullet points...`,
  t`Checking if your skills are real...`,
  t`Analyzing ATS compatibility...`,
  t`Writing a verdict fit for a king...`,
];

const getSeverityConfig = () => ({
  critical: {
    icon: XCircle,
    color: "text-red-500",
    bg: "bg-red-50",
    border: "border-red-100",
    label: t`Critical`,
  },
  warning: {
    icon: Warning,
    color: "text-amber-500",
    bg: "bg-amber-50",
    border: "border-amber-100",
    label: t`Warning`,
  },
  suggestion: {
    icon: LightbulbFilament,
    color: "text-blue-500",
    bg: "bg-blue-50",
    border: "border-blue-100",
    label: t`Suggestion`,
  },
});

export const RoastSection = () => {
  const [state, setState] = useState<"idle" | "loading" | "results" | "error">("idle");
  const [result, setResult] = useState<StylionRoastResult | null>(null);
  const [messageIdx, setMessageIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const { resume } = useResumeStore();
  const { tokenBalance } = useCentralizedTokens();
  const { showTokenModal } = useTokenModal();
  const cost = TOKEN_COSTS.RESUME_ROAST;

  const handleRoast = async () => {
    if (!resume.data) return;

    if (tokenBalance !== null && tokenBalance < cost) {
      showTokenModal({
        requiredTokens: cost,
        currentTokens: tokenBalance,
        featureName: t`AI Resume Roast`,
      });
      return;
    }

    setState("loading");
    setMessageIdx(0);

    const interval = setInterval(() => {
      setMessageIdx((i) => (i + 1) % getRoastMessages().length);
    }, 1800);

    try {
      const locale = resume.data.metadata?.locale ?? "en-US";
      const data = await agentsService.roastResume(resume.data, locale);
      setResult(data);
      setState("results");
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || "";
      if (msg.toLowerCase().includes("insufficient") || msg.toLowerCase().includes("token")) {
        showTokenModal({
          requiredTokens: cost,
          currentTokens: tokenBalance ?? 0,
          featureName: t`AI Resume Roast`,
          onRetry: handleRoast,
        });
        setState("idle");
      } else {
        setErrorMsg(msg || t`Something went wrong. Please try again.`);
        setState("error");
      }
    } finally {
      clearInterval(interval);
    }
  };

  const handleReset = () => {
    setState("idle");
    setResult(null);
    setErrorMsg("");
  };

  if (state === "loading") {
    return (
      <div className="flex flex-col items-center justify-center gap-6 px-6 py-16 text-center">
        <div className="relative flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 to-red-100">
          <span className="animate-bounce text-5xl">🦁</span>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-800">{t`Stylion is judging...`}</p>
          <p className="text-xs text-gray-500 transition-all duration-500">{getRoastMessages()[messageIdx]}</p>
        </div>
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="size-2 animate-bounce rounded-full bg-orange-400"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <span className="text-4xl">😿</span>
        <p className="text-sm text-gray-600">{errorMsg}</p>
        <button
          onClick={handleReset}
          className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
        >
          <ArrowCounterClockwise size={14} />
          {t`Try Again`}
        </button>
      </div>
    );
  }

  if (state === "results" && result) {
    return <RoastResults result={result} onReset={handleReset} />;
  }

  // Idle state
  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Header */}
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-gradient-to-br from-orange-50 to-red-50 px-5 py-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 shadow-lg shadow-orange-200">
          <span className="text-3xl">🦁</span>
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-900">{t`AI Resume Roast`}</h2>
          <p className="mt-1 text-[12px] leading-relaxed text-gray-500">
            {t`Let Stylion, our royal AI critic, tear apart your resume with sharp wit and actionable feedback.`}
          </p>
        </div>
      </div>

      {/* What you get */}
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`What you'll get`}</p>
        {[
          { icon: "🎯", label: t`ATS Score (0–100)` },
          { icon: "👑", label: t`Royal Rank & Title` },
          { icon: "🔥", label: t`Sarcastic One-Liner` },
          { icon: "📋", label: t`Detailed Critique by Category` },
          { icon: "⚖️", label: t`Royal Verdict` },
        ].map(({ icon, label }) => (
          <div key={label} className="flex items-center gap-2.5 rounded-lg bg-gray-50 px-3 py-2">
            <span className="text-base">{icon}</span>
            <span className="text-[13px] text-gray-700">{label}</span>
          </div>
        ))}
      </div>

      {/* Token cost + button */}
      <div className="space-y-2">
        <p className="text-center text-[11px] text-gray-400">
          {t`Costs`}{" "}
          <span className="font-bold text-orange-500">{cost}</span>{" "}
          {t`tokens`}
          {tokenBalance !== null && (
            <span className="ml-1 text-gray-400">
              ({t`you have`} <span className={cn("font-semibold", tokenBalance < cost ? "text-red-500" : "text-green-600")}>{tokenBalance}</span>)
            </span>
          )}
        </p>
        <button
          onClick={handleRoast}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 font-semibold text-white shadow-md shadow-orange-200 transition-all hover:from-orange-600 hover:to-red-600 hover:shadow-lg active:scale-95"
        >
          <Fire size={18} weight="fill" />
          {t`Roast My Resume`} 🔥
        </button>
      </div>
    </div>
  );
};

const RoastResults = ({ result, onReset }: { result: StylionRoastResult; onReset: () => void }) => {
  const isAr = result.language === "ar";
  const atsColor =
    result.ats_score >= 70 ? "text-green-600" : result.ats_score >= 40 ? "text-amber-500" : "text-red-500";
  const atsRingColor =
    result.ats_score >= 70 ? "#22c55e" : result.ats_score >= 40 ? "#f59e0b" : "#ef4444";

  const circumference = 2 * Math.PI * 28;
  const offset = circumference - (result.ats_score / 100) * circumference;

  return (
    <div className="flex flex-col gap-4 p-5">
      {/* ATS Score + Rank row */}
      <div className="flex items-center gap-4 rounded-2xl bg-gray-50 p-4">
        {/* Score ring */}
        <div className="relative shrink-0">
          <svg width="64" height="64" className="-rotate-90">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="5" />
            <circle
              cx="32" cy="32" r="28" fill="none"
              stroke={atsRingColor} strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={cn("text-lg font-black leading-none", atsColor)}>{result.ats_score}</span>
            <span className="text-[8px] font-medium text-gray-400">ATS</span>
          </div>
        </div>

        {/* Rank */}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Your Rank`}</p>
          <p className="truncate text-sm font-bold text-gray-800">
            {result.rank.emoji} {isAr ? result.rank.ar : result.rank.en}
          </p>
          <p className="mt-0.5 text-[11px] leading-tight text-gray-500 line-clamp-2">
            {isAr ? result.rank.description_ar : result.rank.description_en}
          </p>
        </div>
      </div>

      {/* Roast Quote */}
      <div className="relative rounded-xl bg-gradient-to-br from-orange-50 to-red-50 px-4 py-3">
        <span className="absolute -top-2 left-3 text-2xl">🦁</span>
        <blockquote className="mt-1 text-[13px] italic leading-relaxed text-gray-700">
          "{result.roast_quote}"
        </blockquote>
      </div>

      {/* Scores row */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{t`Creativity`}</p>
          <p className="mt-0.5 text-[13px] font-bold text-gray-800">
            {isAr ? result.creativity_score.ar : result.creativity_score.en}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{t`Keywords`}</p>
          <p className="mt-0.5 text-[13px] font-bold text-gray-800">
            {isAr ? result.keyword_score.ar : result.keyword_score.en}
          </p>
        </div>
      </div>

      {/* Detailed critique */}
      {result.detailed_critique.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Issues Found`}</p>
          {result.detailed_critique.map((item, i) => {
            const cfg = getSeverityConfig()[item.severity] ?? getSeverityConfig().suggestion;
            const Icon = cfg.icon;
            return (
              <div key={i} className={cn("rounded-xl border p-3", cfg.bg, cfg.border)}>
                <div className="flex items-start gap-2">
                  <Icon size={15} weight="fill" className={cn("mt-0.5 shrink-0", cfg.color)} />
                  <div className="min-w-0 space-y-1">
                    <p className="text-[12px] font-semibold text-gray-800">
                      <span className="text-gray-400">{item.category}: </span>
                      {item.issue}
                    </p>
                    <p className="text-[11px] italic text-gray-500">"{item.stylion_says}"</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Overall verdict */}
      {result.overall_verdict && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t`Stylion's Verdict`}
          </p>
          <div className="rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 p-4">
            <p className="text-[13px] leading-relaxed text-gray-100">{result.overall_verdict}</p>
          </div>
        </div>
      )}

      {/* Roast again button */}
      <button
        onClick={onReset}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-orange-200 bg-white py-2.5 text-sm font-semibold text-orange-500 transition hover:bg-orange-50"
      >
        <ArrowCounterClockwise size={15} />
        {t`Roast Again`}
      </button>
    </div>
  );
};
